import { useTranslation } from "react-i18next";
import { useWebAudioPlayer, WebAudioSource } from "core/hooks/useWebAudioPlayer/useWebAudioPlayer";
import ErrorAlert from "shared/UI/Alerts/ErrorAlert";
import { AudioPlayerControlBar } from "../../../../Resources/components/ResourceViewer/components/MediaPlayer/AudioPlayerControlBar/AudioPlayerControlBar";
import { useTrackAudioPlayStart } from "../../../hooks/useTrackAudioPlayStart/useTrackAudioPlayStart";
import { AudioPlayerResource } from "../../../enums/types";
import Button from "shared/UI/Buttons/Button";
import { trackAudioPlayerInteraction, trackAudioPlayerSeek } from "core/monitoring/Events";

type DesktopAudioPlayerProps = {
  resource: AudioPlayerResource;
  onDone: () => void;
};

export const DesktopAudioPlayer = ({ resource, onDone }: DesktopAudioPlayerProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: "audioPlayer" });
  const player = useWebAudioPlayer();
  const trackedPlay = useTrackAudioPlayStart(player.play);

  const handlePlay = () => {
    trackAudioPlayerInteraction("play");
    trackedPlay();
  };

  const handlePause = () => {
    trackAudioPlayerInteraction("pause");
    player.pause();
  };

  const handleSeekEnd = () => {
    trackAudioPlayerSeek(player.playTime, player.duration);
    player.onSeekMouseUp();
  };

  const handleDone = () => {
    trackAudioPlayerInteraction("done");
    onDone();
  };

  return (
    <>
      <div data-testid="desktop-audio-player" className="flex items-center gap-4 rounded-lg bg-white py-6">
        <WebAudioSource src={resource.src} player={player} />
        <div className="min-w-0 flex-1 space-y-4">
          <h2 className="truncate font-bold text-font-dark">{resource.title}</h2>
          <AudioPlayerControlBar
            disabled={!player.isInitialised}
            position={player.duration > 0 ? player.playTime / player.duration : 0}
            duration={player.duration}
            isPlaying={player.isPlaying}
            playLabel={t("playLabel")}
            pauseLabel={t("pauseLabel")}
            play={handlePlay}
            pause={handlePause}
            onSeekMouseDown={player.onSeekMouseDown}
            onSeekMouseUp={handleSeekEnd}
            onSeekChange={player.onSeekChange}
          />
        </div>

        {player.error && <ErrorAlert content={t("loadingError")} />}
      </div>{" "}
      <Button className="mt-6" text={t("done")} onClick={handleDone} />
    </>
  );
};
